import { memo } from "react";
import { Box, Button, Skeleton, Typography } from "@mui/material";
import { focusRingSx } from "../../overviewPanel";
import { ScreenerLogo } from "../../../Screener/ScreenerLogo";
import { cardSx, TABULAR, type TonalTokens } from "../../../polish/tokens";
import type { WatchlistEarningsEntry } from "../../../../hooks/useWatchlistEarnings";
import { SECTION_TITLE_GAP, sectionTitleSx } from "./headings";
import type { UpcomingDay } from "./thisWeekModel";

export const THIS_WEEK_EMPTY = "No reports this week";

const ROW_HEIGHT = 44;
const LOGO = 24;

interface UpcomingRowProps {
  entry: WatchlistEarningsEntry;
  tokens: TonalTokens;
  onOpenCompany: (ticker: string) => void;
}

/** One company reporting: logo, ticker and name; it opens the company's earnings. */
const UpcomingRow = memo(
  ({ entry, tokens, onOpenCompany }: UpcomingRowProps) => (
    <Box
      component="button"
      type="button"
      data-testid="this-week-row"
      aria-label={`Open ${entry.companyName} earnings`}
      onClick={() => onOpenCompany(entry.ticker)}
      sx={{
        all: "unset",
        ...focusRingSx,
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        width: "100%",
        minHeight: ROW_HEIGHT,
        px: 1.5,
        borderRadius: "8px",
        cursor: "pointer",
        WebkitTapHighlightColor: "transparent",
        transition: "background-color 120ms ease",
        "@media (hover: hover)": {
          "&:hover": { backgroundColor: tokens.raised },
          "&:hover .identity-ticker": { color: tokens.gain },
        },
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      }}
    >
      <ScreenerLogo
        symbol={entry.ticker}
        name={entry.companyName}
        size={LOGO}
      />
      <Typography
        component="span"
        className="identity-ticker"
        noWrap
        sx={{
          flex: "none",
          fontSize: "0.875rem",
          lineHeight: "20px",
          fontWeight: 600,
          color: tokens.text,
        }}
      >
        {entry.ticker}
      </Typography>
      <Typography
        component="span"
        noWrap
        sx={{
          minWidth: 0,
          fontSize: "0.8125rem",
          lineHeight: "20px",
          color: tokens.muted,
        }}
      >
        {entry.companyName}
      </Typography>
    </Box>
  ),
);
UpcomingRow.displayName = "UpcomingRow";

const dayHeaderSx = (tokens: TonalTokens) =>
  ({
    ...TABULAR,
    fontSize: "0.8125rem",
    lineHeight: "20px",
    color: tokens.muted,
    px: 1.5,
    pt: 1,
    pb: 0.25,
  }) as const;

interface ThisWeekSkeletonProps {
  tokens: TonalTokens;
}

/** The card's final shape while the dates load: a day and three rows. */
const ThisWeekSkeleton = ({ tokens }: ThisWeekSkeletonProps) => (
  <Box data-testid="this-week-skeleton">
    <Box sx={dayHeaderSx(tokens)}>
      <Skeleton width={72} height={20} />
    </Box>
    {[0, 1, 2].map((at) => (
      <Box
        key={at}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          minHeight: ROW_HEIGHT,
          px: 1.5,
        }}
      >
        <Skeleton
          variant="rounded"
          width={LOGO}
          height={LOGO}
          sx={{ borderRadius: "6px", flex: "none" }}
        />
        <Skeleton width={44} height={20} />
        <Skeleton width="40%" height={18} />
      </Box>
    ))}
  </Box>
);

interface ThisWeekProps {
  /** Grouped by day, without the reports already in Reported. */
  days: UpcomingDay[];
  /** No dates yet: the skeleton. */
  loading: boolean;
  /** The first load failed (dates already shown stay through a failed refetch). */
  failed: boolean;
  onRetry: () => void;
  tokens: TonalTokens;
  onOpenCompany: (ticker: string) => void;
}

/**
 * Your companies reporting this week, a group per day; each moves to
 * Reported once its card is in the feed. Label and number only.
 */
export const ThisWeek = ({
  days,
  loading,
  failed,
  onRetry,
  tokens,
  onOpenCompany,
}: ThisWeekProps) => (
  <Box
    component="section"
    aria-labelledby="this-week-heading"
    data-testid="this-week"
  >
    <Typography
      id="this-week-heading"
      component="h2"
      sx={{ ...sectionTitleSx(tokens), mb: SECTION_TITLE_GAP }}
    >
      This week
    </Typography>
    <Box sx={{ ...cardSx(tokens), px: 0.5, py: 0.75 }}>
      {loading && <ThisWeekSkeleton tokens={tokens} />}

      {!loading && failed && (
        <Box
          data-testid="this-week-error"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            minHeight: ROW_HEIGHT,
            pl: 1.5,
          }}
        >
          <Typography sx={{ fontSize: "0.8125rem", color: tokens.muted }}>
            Couldn't load this week
          </Typography>
          <Button
            onClick={onRetry}
            sx={{
              minHeight: 44,
              px: 2,
              borderRadius: "999px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.8125rem",
              color: tokens.muted,
              "@media (hover: hover)": {
                "&:hover": {
                  color: tokens.text,
                  backgroundColor: tokens.raised,
                },
              },
              ...focusRingSx,
            }}
          >
            Try again
          </Button>
        </Box>
      )}

      {!loading && !failed && days.length === 0 && (
        <Typography
          data-testid="this-week-empty"
          sx={{
            fontSize: "0.8125rem",
            lineHeight: `${ROW_HEIGHT}px`,
            color: tokens.faint,
            px: 1.5,
          }}
        >
          {THIS_WEEK_EMPTY}
        </Typography>
      )}

      {!loading &&
        !failed &&
        days.map((group) => (
          <Box key={group.day} component="section" aria-label={group.label}>
            <Typography
              component="h3"
              data-testid="this-week-day"
              sx={dayHeaderSx(tokens)}
            >
              {group.label}
            </Typography>
            {group.entries.map((entry) => (
              <UpcomingRow
                key={entry.ticker}
                entry={entry}
                tokens={tokens}
                onOpenCompany={onOpenCompany}
              />
            ))}
          </Box>
        ))}
    </Box>
  </Box>
);
